// Sentry initialization should be imported first!
import '../instrument';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { BrowserRouter, Route, Routes } from 'react-router';
import LoginPage from './pages/auth/LoginPage.tsx';
import { ThemeProvider } from './helpers/ThemeContext.tsx';
import { ToastContainer } from 'react-toastify';
import RegisterPage from './pages/auth/RegisterPage.tsx';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage.tsx';
import ResetPasswordPage from './pages/auth/ResetPasswordPage.tsx';
import PrivacyPolicyPage from './pages/common/privacy-policy.tsx';
import TermsPage from './pages/common/terms-of-use.tsx';
import { UserProvider } from './helpers/UserContext.tsx';
import Alpacas from './pages/alpaca/alpacas.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AlpacaPage from './pages/alpaca/AlpacaPage.tsx';
import UpdateAlpaca from './pages/alpaca/UpdateAlpaca.tsx';
import Farms from './pages/farms/Farms.tsx';
import FarmPage from './pages/farms/FarmPage.tsx';
import UpdateFarm from './pages/farms/UpdateFarm.tsx';
import Contacts from './pages/contacts/Contacts.tsx';
import ContactPage from './pages/contacts/ContactPage.tsx';
import UpdateContact from './pages/contacts/UpdateContact.tsx';
import Events from './pages/events/Events.tsx';
import EventPage from './pages/events/EventPage.tsx';
import UpdateEvent from './pages/events/UpdateEvent.tsx';
import EditAlpacaEvent from './pages/events/EditAlpacaEvent.tsx';
import GalleryPage from './pages/gallery/Gallery.tsx';
import PublicGalleryPage from './pages/gallery/PublicGallery.tsx';
import Settings from './pages/settings/settings.tsx';
import * as Sentry from '@sentry/react';
import RequireAuth from './components/RequireAuth.tsx';
import AdminPage from './pages/admin/AdminPage.tsx';
import UserPage from './pages/admin/UserPage.tsx';
import { UserSubscriptionsProvider } from './queries/SubscriptionQueries.tsx';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20 * 1000, // 20 seconds
    },
  },
});

createRoot(document.getElementById('root')!, {
  //Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <UserSubscriptionsProvider>
            <BrowserRouter>
              <SentryRoutes>
                <Route path='/' element={<LoginPage />} />
                <Route path='/register' element={<RegisterPage />} />
                <Route path='/forgotpassword' element={<ForgotPasswordPage />} />
                <Route path='/resetpassword' element={<ResetPasswordPage />} />
                <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />
                <Route path='/terms-of-use' element={<TermsPage />} />
                <Route
                  path='/alpacas'
                  element={
                    <RequireAuth>
                      <Alpacas />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/alpacas/:id'
                  element={
                    <RequireAuth>
                      <AlpacaPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/add-alpaca'
                  element={
                    <RequireAuth>
                      <UpdateAlpaca />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/update-alpaca/:id'
                  element={
                    <RequireAuth>
                      <UpdateAlpaca />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/farms'
                  element={
                    <RequireAuth>
                      <Farms />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/farms/:id'
                  element={
                    <RequireAuth>
                      <FarmPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/add-farm'
                  element={
                    <RequireAuth>
                      <UpdateFarm />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/update-farm/:id'
                  element={
                    <RequireAuth>
                      <UpdateFarm />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/contacts'
                  element={
                    <RequireAuth>
                      <Contacts />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/contacts/:id'
                  element={
                    <RequireAuth>
                      <ContactPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/add-contact'
                  element={
                    <RequireAuth>
                      <UpdateContact />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/update-contact/:id'
                  element={
                    <RequireAuth>
                      <UpdateContact />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/events'
                  element={
                    <RequireAuth>
                      <Events />
                    </RequireAuth>
                  }
                />

                <Route
                  path='/events/:id'
                  element={
                    <RequireAuth>
                      <EventPage />
                    </RequireAuth>
                  }
                >
                  <Route
                    path='alpaca-event/:alpacaEventId'
                    element={
                      <RequireAuth>
                        <EditAlpacaEvent />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route
                  path='/update-event/:id'
                  element={
                    <RequireAuth>
                      <UpdateEvent />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/add-event'
                  element={
                    <RequireAuth>
                      <UpdateEvent />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/gallery'
                  element={
                    <RequireAuth>
                      <GalleryPage />
                    </RequireAuth>
                  }
                />
                <Route path='/gallery/:id' element={<PublicGalleryPage />} />
                <Route
                  path='/settings'
                  element={
                    <RequireAuth>
                      <Settings />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/admin'
                  element={
                    <RequireAuth adminOnly={true}>
                      <AdminPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/admin/:id'
                  element={
                    <RequireAuth adminOnly={true}>
                      <UserPage />
                    </RequireAuth>
                  }
                />
              </SentryRoutes>
            </BrowserRouter>
            <ToastContainer
              position='bottom-center'
              autoClose={3000}
              limit={3}
              closeOnClick
              theme='dark'
            />
          </UserSubscriptionsProvider>
        </UserProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
